import {
  Injectable,
  inject,
  DestroyRef,
  RendererFactory2,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Hotkey binding. `keys` is a chord description in our shorthand:
 *   - "Mod+K"     → Cmd+K on macOS, Ctrl+K on Windows / Linux
 *   - "Mod+Shift+S" / "Shift+?" / "?"
 *
 * `handler` is called when the chord fires. `preventDefault` controls whether
 * the browser's default action for the key is suppressed (default true so
 * Ctrl+K does not focus the URL bar).
 *
 * Bindings are removed by calling the returned disposer or by passing the
 * caller's `DestroyRef` to `register({ until })`.
 */
export interface HotkeyBinding {
  readonly keys: string;
  readonly handler: (event: KeyboardEvent) => void;
  readonly description?: string;
  readonly preventDefault?: boolean;
  readonly allowInInputs?: boolean;
}

/** Disposer returned from `register()` — call to remove the binding. */
export type HotkeyDisposer = () => void;

interface ParsedChord {
  readonly mod: boolean;
  readonly ctrl: boolean;
  readonly shift: boolean;
  readonly alt: boolean;
  /** Lowercased key (matches KeyboardEvent.key.toLowerCase()). */
  readonly key: string;
}

const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * Application-wide hotkey registry. Document-level keydown listener
 * dispatches to registered bindings. Scope-aware (per-binding
 * `allowInInputs`) and SSR-safe (no-op on the server).
 *
 * Use {@link HotkeyService.register} for an explicit disposer, or pass
 * the caller's DestroyRef via the `until` option.
 */
@Injectable({ providedIn: 'root' })
export class HotkeyService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly renderer = inject(RendererFactory2).createRenderer(null, null);

  private readonly bindings = new Map<string, HotkeyBinding>();
  private readonly isMac = (() => {
    if (!isPlatformBrowser(this.platformId)) return false;
    const platform = (navigator as { userAgentData?: { platform?: string }; platform?: string })
      .userAgentData?.platform ?? navigator.platform ?? '';
    return platform.toUpperCase().includes('MAC');
  })();

  private removeListener: (() => void) | null = null;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.removeListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
      this.onKeydown(event);
    });
    this.destroyRef.onDestroy(() => {
      this.removeListener?.();
      this.removeListener = null;
      this.bindings.clear();
    });
  }

  /**
   * Register a binding. Returns a disposer that removes the binding.
   * If `until` is provided, the binding is removed automatically when
   * that DestroyRef fires.
   */
  register(binding: HotkeyBinding, options?: { until?: DestroyRef }): HotkeyDisposer {
    const key = this.normalise(binding.keys);
    this.bindings.set(key, binding);
    const dispose = () => {
      if (this.bindings.get(key) === binding) {
        this.bindings.delete(key);
      }
    };
    options?.until?.onDestroy(dispose);
    return dispose;
  }

  /** Human-readable modifier prefix for the current platform ("⌘" on Mac, "Ctrl+" elsewhere). */
  modifierLabel(): string {
    return this.isMac ? '⌘' : 'Ctrl+';
  }

  private onKeydown(event: KeyboardEvent): void {
    // Honour `allowInInputs` per binding — skip otherwise when focus is in a field.
    const target = event.target as HTMLElement | null;
    const inInput =
      target?.tagName != null &&
      (INPUT_TAGS.has(target.tagName) || target.isContentEditable);

    for (const binding of this.bindings.values()) {
      const chord = this.parseChord(binding.keys);
      if (!this.matches(event, chord)) continue;
      if (inInput && !binding.allowInInputs) continue;
      if (binding.preventDefault !== false) event.preventDefault();
      binding.handler(event);
      return; // single dispatch per keydown
    }
  }

  private parseChord(spec: string): ParsedChord {
    const parts = spec.split('+').map(p => p.trim());
    let mod = false;
    let ctrl = false;
    let shift = false;
    let alt = false;
    let key = '';
    for (const part of parts) {
      const lower = part.toLowerCase();
      if (lower === 'mod') mod = true;
      else if (lower === 'ctrl' || lower === 'control') ctrl = true;
      else if (lower === 'shift') shift = true;
      else if (lower === 'alt' || lower === 'option') alt = true;
      else key = lower;
    }
    return { mod, ctrl, shift, alt, key };
  }

  private matches(event: KeyboardEvent, chord: ParsedChord): boolean {
    // Synthetic / IME / dead-key / composition events can fire keydown with an
    // undefined `key`. Treat those as a non-match rather than dereferencing
    // `undefined.toLowerCase()` (which throws). The shortcut simply does not fire.
    if (!event.key) return false;
    if (event.key.toLowerCase() !== chord.key) return false;
    if (chord.shift !== event.shiftKey) return false;
    if (chord.alt !== event.altKey) return false;
    if (chord.mod) {
      // "Mod" = Cmd on Mac, Ctrl elsewhere
      const modPressed = this.isMac ? event.metaKey : event.ctrlKey;
      if (!modPressed) return false;
    } else if (chord.ctrl) {
      if (!event.ctrlKey) return false;
    } else {
      if (event.metaKey || event.ctrlKey) return false;
    }
    return true;
  }

  private normalise(spec: string): string {
    return spec.toLowerCase().split('+').map(s => s.trim()).sort().join('+');
  }
}
